import { AppDispatch } from '@/redux/store';
import { AuctionsNearMeQueryParams } from '../types/AuctionsNearMe.types';
import { createBoundingBoxString } from './createBoundingBoxString';
import { fetchCatalogsNearMeByBounds } from '@/redux/modules/auctionsNearMe/auctionsNearMe.actions';
import { geocodeLocation } from './geocodeLocation.api';
import { MapBounds } from '../components/MapView/components/GoogleMap/AuctionsNearMeGoogleMap';
import { useEffect } from 'react';

type params = {
    currentActiveBoundsRef: { current: MapBounds | null };
    currentPage: number;
    customSearchBounds: MapBounds | null;
    dispatch: AppDispatch;
    googleMapsReady: boolean;
    pageSize: number;
    queryParams: AuctionsNearMeQueryParams;
    setCustomSearchBounds: (bounds: MapBounds | null) => void;
    setGeocoderFailed: (failed: boolean) => void;
    setIsGeocoding: (isGeocoding: boolean) => void;
    setSearchTerm: (term: string) => void;
    // TODO: Remove feature flag once new auctions near me page is fully deployed
    // @see https://liveauctioneers.atlassian.net/browse/SG-229
    useNewAuctionsNearMePage: boolean;
};

/**
 * Custom hook to geocode custom search term from query params
 * This only runs for direct navigation (e.g., /find-auctions-near-me?search=Cleveland)
 * If customSearchBounds already exists, it means the user came from SearchInput, so skip geocoding
 */
export const useGeocodeCustomSearch = ({
    currentActiveBoundsRef,
    currentPage,
    customSearchBounds,
    dispatch,
    googleMapsReady,
    pageSize,
    queryParams,
    setCustomSearchBounds,
    setGeocoderFailed,
    setIsGeocoding,
    setSearchTerm,
    useNewAuctionsNearMePage,
}: params) => {
    useEffect(() => {
        if (!useNewAuctionsNearMePage || !queryParams.search || !googleMapsReady || customSearchBounds) {
            return;
        }

        const google = typeof window !== 'undefined' ? window.google : null;

        if (!google) {
            return;
        }

        const handleCustomSearch = async () => {
            setIsGeocoding(true);

            const bounds = await geocodeLocation(google, queryParams.search);

            if (bounds) {
                // Geocoding succeeded
                setGeocoderFailed(false);
                setSearchTerm(queryParams.search);
                setCustomSearchBounds(bounds);
                // Store bounds in ref for pagination to use
                currentActiveBoundsRef.current = bounds;

                // Fetch catalogs for the geocoded location
                const insideBoundingBox = createBoundingBoxString(bounds);
                dispatch(
                    fetchCatalogsNearMeByBounds({
                        insideBoundingBox,
                        page: currentPage,
                        pageSize: pageSize,
                    })
                );
            } else {
                // Geocoding failed
                setGeocoderFailed(true);
                setSearchTerm(queryParams.search);
            }

            setIsGeocoding(false);
        };

        handleCustomSearch();
    }, [
        useNewAuctionsNearMePage,
        queryParams.search,
        googleMapsReady,
        customSearchBounds,
        currentPage,
        pageSize,
        dispatch,
        setCustomSearchBounds,
        setGeocoderFailed,
        setIsGeocoding,
        setSearchTerm,
        currentActiveBoundsRef,
    ]);
};
